import axios from "axios";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080";

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Send cookies with requests (needed for test auth and cross-origin requests)
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Don't set Content-Type for multipart/form-data (browser will set it with boundary)
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    try {
      if (window.localStorage.getItem("force_human_challenge") === "true") {
        config.headers["X-Test-Force-Challenge"] = "true";
      }
    } catch (err) {
      console.warn("Failed to read test challenge flag", err);
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Extract request ID from response headers
    const requestID = error.response?.headers?.["x-request-id"] || error.response?.headers?.["X-Request-Id"];
    
    // Attach request ID to error for error boundary and logging
    if (requestID && typeof error === 'object') {
      (error as { requestID?: string }).requestID = requestID;
    }

    // Handle early access requirement
    if (
      typeof window !== "undefined" &&
      error.response?.status === 403 &&
      error.response.data?.code === "EARLY_ACCESS_REQUIRED"
    ) {
      try {
        window.localStorage.setItem(
          "early_access_payload",
          JSON.stringify(error.response.data),
        );
      } catch (storageError) {
        console.warn("Failed to store early access payload", storageError);
      }

      if (window.location.pathname !== "/early-access") {
        window.location.href = "/early-access";
      }
    }

    // Handle unauthorized (401) - redirect to login
    // Skip redirect in test mode (test auth middleware handles authentication)
    const isTestMode = process.env.NEXT_PUBLIC_TEST_MODE === 'true';
    if (
      typeof window !== "undefined" &&
      error.response?.status === 401 &&
      !isTestMode
    ) {
      // Don't redirect if we're already on an auth page (including /auth/email, /auth/callback, etc.)
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath.startsWith("/auth");
      
      // Don't redirect if we're on a public page that should be accessible without login
      const publicPages = [
        "/",
        "/listings",
        "/explore",
        "/vendors",
        "/vendors/",
        "/marketing",
        "/branding",
        "/vision",
        "/policies",
        "/policies/refund",
      ];
      const isPublicPage = publicPages.some(page => 
        currentPath === page || currentPath.startsWith(page + "/")
      );
      
      // Don't redirect for public API endpoints either
      // Note: error.config?.url is relative to the axios baseURL, so it's just "/listings" not "/api/listings"
      const requestPath = error.config?.url || "";
      const publicApiEndpoints = [
        "/public/",
        "/listings",
        "/vendors/slug/",
        "/vendors/apply",
        "/platform-settings/soft-launch",
      ];
      const isPublicApiEndpoint = publicApiEndpoints.some(endpoint => 
        requestPath.includes(endpoint)
      );
      
      if (!isAuthPage && !isPublicPage && !isPublicApiEndpoint) {
        // Store current path for redirect after login
        const fullPath = currentPath + window.location.search;
        try {
          window.localStorage.setItem("redirect_after_login", fullPath);
        } catch (storageError) {
          console.warn("Failed to store redirect path", storageError);
        }
        
        // Redirect to login
        window.location.href = "/auth";
      }
    }

    // Handle rate limiting (429 Too Many Requests)
    if (
      typeof window !== "undefined" &&
      error.response?.status === 429
    ) {
      const data = error.response.data as { message?: string; code?: string; error?: { message?: string; request_id?: string } } | undefined;
      const message = data?.error?.message || data?.message || "You're doing that too quickly. Please wait a moment and try again.";
      
      // For critical flows like payments/checkout, provide more context
      if (error.config?.url?.includes("/checkout") || error.config?.url?.includes("/bookings")) {
        error.rateLimitMessage = "We're processing too many booking requests right now. Please wait a few seconds and try again. If you're unsure whether your last attempt succeeded, check your email or bookings page before retrying.";
      } else if (error.config?.url?.includes("/conversations") || error.config?.url?.includes("/messages")) {
        error.rateLimitMessage = "You've sent a lot of messages in a short time. Please wait briefly before sending more.";
      } else {
        error.rateLimitMessage = message;
      }
    }

    // Handle server errors (500+) - include request ID in message
    if (
      typeof window !== "undefined" &&
      error.response?.status >= 500
    ) {
      const data = error.response.data as { error?: { request_id?: string; message?: string } } | undefined;
      const errorRequestID = data?.error?.request_id || requestID;
      const message = data?.error?.message || "Something went wrong on our end. Please try again.";
      
      if (errorRequestID) {
        error.serverErrorMessage = `${message} If this keeps happening, contact support with request ID: ${errorRequestID}`;
      } else {
        error.serverErrorMessage = message;
      }
    }

    // Handle forbidden (403) - show permission error
    if (
      typeof window !== "undefined" &&
      error.response?.status === 403 &&
      error.response.data?.code !== "EARLY_ACCESS_REQUIRED"
    ) {
      const data = error.response.data as { error?: { message?: string } } | undefined;
      error.forbiddenMessage = data?.error?.message || "You don't have permission to perform this action.";
    }

    return Promise.reject(error);
  },
);

export default api;
